<template>
  <div class="subscription-management">
    <!-- Student Claim Mode (accessed via shared link) -->
    <template v-if="mode === 'claim'">
      <el-card class="claim-card">
        <div class="claim-header">
          <h2>Subscription Access</h2>
          <p class="claim-subtitle">Claim your subscription quota from your school</p>
        </div>

        <!-- Not logged in -->
        <div v-if="!userType" class="claim-login-prompt">
          <el-alert
            title=""
            type="info"
            show-icon
            :closable="false"
            class="login-alert"
          >
            <template slot="title">
              Please <el-button type="text" @click="loginDialogVisible = true">log in</el-button> to claim your subscription
            </template>
          </el-alert>
        </div>

        <!-- Logged in: waiting for eligibility check -->
        <div v-else-if="claimEligible === null" class="claim-checking">
          <el-result icon="info" title="Checking eligibility...">
            <template slot="subTitle">
              <p>Please wait while we verify your access.</p>
            </template>
          </el-result>
        </div>

        <!-- Logged in but not eligible -->
        <div v-else-if="claimEligible === false" class="claim-ineligible">
          <el-result icon="error" title="You're not eligible for this link.">
            <template slot="subTitle">
              <p>This claim link is only available to members of the sharing club.</p>
            </template>
          </el-result>
        </div>

        <!-- Logged in and eligible -->
        <div v-else class="claim-content">
          <div class="claim-stats">
            <div class="stat-item">
              <span class="label">Available Quotas</span>
              <span class="value">{{ quotaStats.available || 0 }}</span>
            </div>
          </div>

          <!-- Student has active subscription -->
          <div v-if="studentActiveQuota" class="student-subscription-status">
            <el-result icon="success" title="You have an active subscription">
              <template slot="subTitle">
                <p>Your subscription is active until <strong>{{ formatDate(studentActiveQuota.expiry_time * 1000) }}</strong></p>
                <p>Remaining: <strong>{{ getRemainingDays(studentActiveQuota) }} days</strong></p>
              </template>
            </el-result>
          </div>

          <!-- No active subscription -->
          <div v-else class="student-no-subscription">
            <div v-if="quotaStats.available > 0" class="claim-action">
              <p>There are available subscription quotas. Click below to claim yours.</p>
              <el-button
                type="primary"
                size="large"
                :loading="claimLoading"
                @click="handleClaimSubscription"
              >
                Get Subscription
              </el-button>
            </div>
            <div v-else class="no-quotas">
              <el-result icon="warning" title="No Available Quotas">
                <template slot="subTitle">
                  <p>There are currently no available subscription quotas. Please contact your advisor.</p>
                </template>
              </el-result>
            </div>
          </div>
        </div>
      </el-card>
    </template>

    <!-- Advisor View -->
    <template v-else>
      <el-card class="management-card">
        <!-- Stats Bar -->
        <div class="stats-bar">
          <div class="stat-card">
            <span class="stat-label">Total Quotas</span>
            <span class="stat-value">{{ quotaStats.total || 0 }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">Available</span>
            <span class="stat-value available">{{ quotaStats.available || 0 }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">Active</span>
            <span class="stat-value active">{{ quotaStats.active || 0 }}</span>
          </div>
          <div class="stat-card">
            <span class="stat-label">Expired</span>
            <span class="stat-value expired">{{ quotaStats.expired || 0 }}</span>
          </div>
        </div>

        <!-- Action Bar -->
        <div class="action-bar">
          <el-button type="primary" @click="handlePurchaseMore">Purchase More</el-button>
          <el-button @click="handleShareClaimLink">Share Claim Link</el-button>
        </div>

        <!-- Tabs -->
        <el-tabs v-model="activeTab">
          <!-- Subscription Quotas Tab -->
          <el-tab-pane label="Subscription Quotas" name="quotas">
            <el-table
              :data="quotas"
              style="width: 100%"
              empty-text="No Data"
              :header-cell-class-name="'custom-head-cell'"
              :row-class-name="'custom-row-bg'"
            >
              <el-table-column type="index" label="#" width="60"></el-table-column>
              <el-table-column prop="student_name" label="Student Name" min-width="140">
                <template slot-scope="scope">
                  <span v-if="scope.row.status !== 'AVAILABLE'">{{ scope.row.student_name || '-' }}</span>
                  <span v-else>-</span>
                </template>
              </el-table-column>
              <el-table-column prop="student_email" label="Student Email" min-width="180">
                <template slot-scope="scope">
                  <span v-if="scope.row.status !== 'AVAILABLE'">{{ scope.row.student_email || '-' }}</span>
                  <span v-else>-</span>
                </template>
              </el-table-column>
              <el-table-column prop="status" label="Status" width="120">
                <template slot-scope="scope">
                  <el-tag :type="getQuotaStatusType(scope.row.status)">
                    {{ scope.row.status }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column prop="duration" label="Duration" width="120">
                <template slot-scope="scope">
                  <span>{{ scope.row.duration || '-' }}</span>
                </template>
              </el-table-column>
              <el-table-column prop="activated_time" label="Activated" width="180">
                <template slot-scope="scope">
                  <span v-if="scope.row.activated_time">{{ formatDate(scope.row.activated_time * 1000) }}</span>
                  <span v-else>-</span>
                </template>
              </el-table-column>
              <el-table-column prop="expiry_time" label="Expires" width="180">
                <template slot-scope="scope">
                  <span v-if="scope.row.expiry_time">{{ formatDate(scope.row.expiry_time * 1000) }}</span>
                  <span v-else>-</span>
                </template>
              </el-table-column>
              <el-table-column label="Remaining Days" width="140" align="center">
                <template slot-scope="scope">
                  <span v-if="scope.row.status === 'ACTIVE'">{{ getRemainingDays(scope.row) }}</span>
                  <span v-else>-</span>
                </template>
              </el-table-column>
              <el-table-column label="Actions" width="120" align="center">
                <template slot-scope="scope">
                  <el-button
                    v-if="scope.row.status === 'AVAILABLE'"
                    type="primary"
                    size="small"
                    @click="openAssignDialog(scope.row)"
                  >
                    ASSIGN
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>

          <!-- Order History Tab -->
          <el-tab-pane label="Order History" name="orders">
            <el-table
              :data="orders"
              style="width: 100%"
              empty-text="No Data"
              :header-cell-class-name="'custom-head-cell'"
              :row-class-name="'custom-row-bg'"
            >
              <el-table-column prop="_id.$id" label="Order ID" width="240"></el-table-column>
              <el-table-column prop="quantity" label="Quantity" width="100"></el-table-column>
              <el-table-column prop="duration" label="Duration" width="120"></el-table-column>
              <el-table-column prop="total_price" label="Total Price" width="120">
                <template slot-scope="scope">
                  ${{ scope.row.total_price }}
                </template>
              </el-table-column>
              <el-table-column prop="order_time" label="Date" width="200">
                <template slot-scope="scope">
                  {{ formatDate(scope.row.order_time * 1000) }}
                </template>
              </el-table-column>
              <el-table-column prop="status" label="Status" width="120">
                <template slot-scope="scope">
                  <el-tag :type="getOrderStatusType(scope.row.status)">
                    {{ scope.row.status }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="Actions" width="120" align="center">
                <template slot-scope="scope">
                  <el-button
                    v-if="scope.row.status === 'PENDING'"
                    type="primary"
                    size="small"
                    @click="goToPayment(scope.row)"
                  >
                    Pay Now
                  </el-button>
                </template>
              </el-table-column>
            </el-table>
          </el-tab-pane>
        </el-tabs>
      </el-card>
    </template>

    <!-- Share Claim Link Dialog -->
    <el-dialog
      title="Share Claim Link"
      :visible.sync="shareDialogVisible"
      width="600px"
    >
      <div class="share-content">
        <div class="share-notes">
          <h4>Important Notes:</h4>
          <ul>
            <li>This link allows students to claim an available subscription quota</li>
            <li>Students will need to log in before claiming</li>
            <li>The link will remain active as long as there are available quotas</li>
          </ul>
        </div>
        <div class="share-url">
          <div class="url-label">Share URL:</div>
          <div class="url-input-group">
            <el-input
              v-model="shareUrl"
              readonly
              size="medium"
            ></el-input>
            <el-button
              type="primary"
              size="medium"
              @click="copyShareUrl"
            >
              Copy URL
            </el-button>
          </div>
        </div>
      </div>
    </el-dialog>

    <!-- Assign Subscription Dialog -->
    <el-dialog
      title="Assign Subscription to Student"
      :visible.sync="assignDialogVisible"
      width="500px"
      :close-on-click-modal="false"
    >
      <div class="assign-content">
        <el-form label-position="top">
          <el-form-item label="Search Student by Email">
            <div class="search-input-group">
              <el-input
                v-model="searchStudentEmail"
                placeholder="Enter student email"
                clearable
                @keyup.enter.native="handleSearchStudent"
              ></el-input>
              <el-button
                type="primary"
                :loading="searchLoading"
                @click="handleSearchStudent"
              >
                Search
              </el-button>
            </div>
          </el-form-item>

          <div v-if="searchResults.length > 0" class="search-results">
            <div class="results-label">Select a student:</div>
            <el-radio-group v-model="selectedStudentId">
              <div
                v-for="student in searchResults"
                :key="student._id.$id"
                class="student-result-item"
              >
                <el-radio :label="student._id.$id">
                  <span class="student-name">{{ student.student_givenName_pinyin }} {{ student.student_lastName_pinyin }}</span>
                  <span class="student-email">{{ student.student_email }}</span>
                </el-radio>
              </div>
            </el-radio-group>
          </div>

          <div v-if="searchPerformed && searchResults.length === 0" class="no-results">
            <el-alert
              title="No students found matching this email"
              type="warning"
              show-icon
              :closable="false"
            ></el-alert>
          </div>
        </el-form>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="assignDialogVisible = false">Cancel</el-button>
        <el-button
          type="primary"
          :disabled="!selectedStudentId"
          :loading="assignLoading"
          @click="handleAssignSubscription"
        >
          Assign
        </el-button>
      </span>
    </el-dialog>

    <!-- Login Dialog (for claim mode) -->
    <el-dialog
      title="Login"
      :visible.sync="loginDialogVisible"
      width="400px"
      :close-on-click-modal="false"
    >
      <login-simple @login-success="handleLoginSuccess" />
    </el-dialog>
  </div>
</template>

<script>
import { postRequest } from '@/api/eclub'
import { getUserId, getUserType, getAccountId } from '@/utils/store'
import LoginSimple from '@/components/LoginSimple'

export default {
  name: 'SubscriptionManagement',
  components: {
    LoginSimple
  },
  data() {
    return {
      accountId: '',
      userType: '',
      mode: '',
      activeTab: 'quotas',
      quotas: [],
      quotaStats: {
        total: 0,
        available: 0,
        active: 0,
        expired: 0
      },
      orders: [],
      studentActiveQuota: null,
      claimEligible: null,
      shareDialogVisible: false,
      shareUrl: '',
      assignDialogVisible: false,
      loginDialogVisible: false,
      selectedQuotaId: '',
      searchStudentEmail: '',
      searchResults: [],
      selectedStudentId: '',
      searchLoading: false,
      searchPerformed: false,
      assignLoading: false,
      claimLoading: false
    }
  },
  computed: {
    isAdvisor() {
      return this.userType === 'advisor' || this.userType === 'schooladmin'
    }
  },
  created() {
    this.userType = getUserType()
    this.mode = this.$route.query.mode || ''
    this.accountId = this.$route.query.account_id || getAccountId() || ''

    if (this.mode === 'claim') {
      this.initClaimMode()
    } else {
      this.initAdvisorMode()
    }
  },
  methods: {
    // ---- Initialization ----
    async initClaimMode() {
      if (!this.userType) return
      this.claimEligible = null
      const eligible = await this.fetchClaimEligibility()
      this.claimEligible = eligible
      if (eligible) {
        this.fetchQuotaStats()
        this.fetchStudentActiveQuota()
      }
    },
    async fetchClaimEligibility() {
      try {
        const response = await postRequest('checkClaimEligibility', {
          student_id: getUserId(),
          account_id: this.accountId
        })
        return !!(response.data && response.data.data && response.data.data.eligible)
      } catch (error) {
        console.error('Error checking claim eligibility:', error)
        return false
      }
    },
    initAdvisorMode() {
      if (!this.accountId) {
        this.fetchAdvisorAccountId()
      } else {
        this.fetchAllData()
      }
    },
    fetchAllData() {
      this.fetchQuotas()
      this.fetchQuotaStats()
      this.fetchOrders()
    },
    async fetchAdvisorAccountId() {
      try {
        const userId = getUserId()
        const response = await postRequest('getAdvisorClubs', { advisor_id: userId })
        if (response.data.code === 0 && response.data.data.length > 0) {
          this.accountId = response.data.data[0].account_id
          this.fetchAllData()
        }
      } catch (error) {
        console.error('Error fetching advisor account:', error)
      }
    },

    // ---- Data Fetching ----
    async fetchQuotas() {
      try {
        const response = await postRequest('getSubscriptionQuotas', {
          account_id: this.accountId
        })
        if (response.data.code === 0) {
          this.quotas = response.data.data
        }
      } catch (error) {
        console.error('Error fetching subscription quotas:', error)
      }
    },
    async fetchQuotaStats() {
      try {
        const response = await postRequest('getSubscriptionQuotaStats', {
          account_id: this.accountId
        })
        if (response.data.code === 0) {
          this.quotaStats = response.data.data
        }
      } catch (error) {
        console.error('Error fetching quota stats:', error)
      }
    },
    async fetchOrders() {
      try {
        const response = await postRequest('getAccountSubscriptionOrders', {
          account_id: this.accountId
        })
        if (response.data.code === 0) {
          this.orders = response.data.data
        }
      } catch (error) {
        console.error('Error fetching subscription orders:', error)
      }
    },
    async fetchStudentActiveQuota() {
      try {
        const userId = getUserId()
        const response = await postRequest('getSubscriptionQuotas', {
          account_id: this.accountId
        })
        if (response.data.code === 0) {
          const allQuotas = response.data.data
          this.studentActiveQuota = allQuotas.find(
            q => q.student_id === userId && q.status === 'ACTIVE'
          ) || null
        }
      } catch (error) {
        console.error('Error fetching student quota:', error)
      }
    },

    // ---- Actions ----
    handlePurchaseMore() {
      this.$router.push({
        path: '/home/subscriptionOrderForm',
        query: { account_id: this.accountId }
      })
    },
    handleShareClaimLink() {
      this.shareUrl = `${window.location.origin}/#/home/subscriptionManagement?account_id=${this.accountId}&mode=claim`
      this.shareDialogVisible = true
    },
    async copyShareUrl() {
      try {
        await navigator.clipboard.writeText(this.shareUrl)
        this.$message.success('URL copied to clipboard')
      } catch (err) {
        console.error('Failed to copy URL:', err)
        this.$message.error('Failed to copy URL')
      }
    },
    openAssignDialog(quota) {
      this.selectedQuotaId = quota._id.$id
      this.searchStudentEmail = ''
      this.searchResults = []
      this.selectedStudentId = ''
      this.searchPerformed = false
      this.assignDialogVisible = true
    },
    async handleSearchStudent() {
      if (!this.searchStudentEmail.trim()) {
        this.$message.warning('Please enter a student email')
        return
      }
      this.searchLoading = true
      this.searchPerformed = false
      try {
        const response = await postRequest('getStudentsByAccount', {
          user_id: getUserId(),
          user_type: getUserType()
        })
        if (response.data.code === 0) {
          const keyword = this.searchStudentEmail.trim().toLowerCase()
          this.searchResults = response.data.data.filter(
            s => s.student_email && s.student_email.toLowerCase().includes(keyword)
          )
        } else {
          this.searchResults = []
        }
        this.searchPerformed = true
      } catch (error) {
        console.error('Error searching students:', error)
        this.$message.error('Failed to search students')
        this.searchResults = []
        this.searchPerformed = true
      } finally {
        this.searchLoading = false
      }
    },
    async handleAssignSubscription() {
      if (!this.selectedStudentId) {
        this.$message.warning('Please select a student')
        return
      }
      this.assignLoading = true
      try {
        const response = await postRequest('assignSubscriptionToStudent', {
          quota_id: this.selectedQuotaId,
          student_id: this.selectedStudentId
        })
        if (response.data.code === 0) {
          this.$message.success('Subscription assigned successfully')
          this.assignDialogVisible = false
          this.fetchQuotas()
          this.fetchQuotaStats()
        } else {
          this.$message.error(response.data.msg || 'Failed to assign subscription')
        }
      } catch (error) {
        console.error('Error assigning subscription:', error)
        this.$message.error('Failed to assign subscription')
      } finally {
        this.assignLoading = false
      }
    },
    async handleClaimSubscription() {
      this.$confirm(
        'Are you sure you want to claim a subscription quota?',
        'Confirm',
        {
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          type: 'info'
        }
      ).then(async () => {
        this.claimLoading = true
        try {
          const response = await postRequest('claimSubscriptionByStudent', {
            student_id: getUserId(),
            account_id: this.accountId
          })
          if (response.data.code === 0) {
            this.$message.success('Subscription claimed successfully')
            this.fetchQuotaStats()
            this.fetchStudentActiveQuota()
          } else {
            this.$message.error(response.data.msg || 'Failed to claim subscription')
          }
        } catch (error) {
          console.error('Error claiming subscription:', error)
          this.$message.error('Failed to claim subscription')
        } finally {
          this.claimLoading = false
        }
      }).catch(() => {
        // User cancelled
      })
    },
    goToPayment(order) {
      this.$router.push({
        path: '/home/subscriptionPayment',
        query: {
          order_id: order._id.$id,
          account_id: this.accountId
        }
      })
    },

    // ---- Login ----
    async handleLoginSuccess() {
      this.loginDialogVisible = false
      this.userType = getUserType()
      await this.$root.$emit('user-logged-in', true)
      await this.$nextTick()

      if (this.mode === 'claim') {
        this.initClaimMode()
      }
    },

    // ---- Helpers ----
    formatDate(timestamp) {
      if (!timestamp) return '-'
      return new Date(timestamp).toLocaleString()
    },
    getQuotaStatusType(status) {
      const types = {
        AVAILABLE: 'info',
        ACTIVE: 'success',
        EXPIRED: 'danger'
      }
      return types[status] || 'info'
    },
    getOrderStatusType(status) {
      const types = {
        PENDING: 'warning',
        COMPLETED: 'success',
        PAID: 'success',
        FAILED: 'danger',
        CANCELLED: 'info'
      }
      return types[status] || 'info'
    },
    getRemainingDays(quota) {
      if (!quota.expiry_time) return '-'
      const now = Math.floor(Date.now() / 1000)
      const remaining = quota.expiry_time - now
      if (remaining <= 0) return '0'
      return Math.ceil(remaining / 86400)
    }
  }
}
</script>

<style lang="scss" scoped>
.subscription-management {
  padding: 20px;

  // ---- Stats Bar ----
  .stats-bar {
    display: flex;
    gap: 20px;
    margin-bottom: 24px;

    .stat-card {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 20px;
      background-color: #f5f7fa;
      border-radius: 8px;

      .stat-label {
        font-size: 14px;
        color: #909399;
        margin-bottom: 8px;
      }

      .stat-value {
        font-size: 28px;
        font-weight: 600;
        color: #303133;

        &.available {
          color: #909399;
        }

        &.active {
          color: #67c23a;
        }

        &.expired {
          color: #f56c6c;
        }
      }
    }
  }

  // ---- Action Bar ----
  .action-bar {
    display: flex;
    gap: 12px;
    margin-bottom: 20px;
  }

  // ---- Claim Mode ----
  .claim-card {
    max-width: 600px;
    margin: 40px auto;

    .claim-header {
      text-align: center;
      margin-bottom: 24px;

      h2 {
        margin: 0 0 8px;
        color: #303133;
        font-size: 24px;
      }

      .claim-subtitle {
        color: #909399;
        font-size: 14px;
        margin: 0;
      }
    }

    .claim-login-prompt {
      margin-top: 20px;
    }

    .claim-stats {
      display: flex;
      justify-content: center;
      padding: 20px;
      background-color: #f5f7fa;
      border-radius: 8px;
      margin-bottom: 24px;

      .stat-item {
        display: flex;
        flex-direction: column;
        align-items: center;

        .label {
          font-size: 14px;
          color: #909399;
          margin-bottom: 4px;
        }

        .value {
          font-size: 28px;
          font-weight: 600;
          color: #303133;
        }
      }
    }

    .student-subscription-status {
      text-align: center;
    }

    .student-no-subscription {
      .claim-action {
        text-align: center;
        padding: 20px 0;

        p {
          color: #606266;
          margin-bottom: 16px;
        }
      }

      .no-quotas {
        text-align: center;
      }
    }
  }
}

// ---- Share Dialog ----
.share-content {
  .share-notes {
    margin-bottom: 24px;

    h4 {
      margin: 0 0 12px;
      color: #303133;
    }

    ul {
      margin: 0;
      padding-left: 20px;
      color: #606266;

      li {
        margin-bottom: 8px;
      }
    }
  }

  .share-url {
    .url-label {
      font-weight: 500;
      margin-bottom: 8px;
      color: #303133;
    }

    .url-input-group {
      display: flex;
      gap: 12px;

      .el-input {
        flex: 1;
      }
    }
  }
}

// ---- Assign Dialog ----
.assign-content {
  .search-input-group {
    display: flex;
    gap: 12px;

    .el-input {
      flex: 1;
    }
  }

  .search-results {
    margin-top: 16px;

    .results-label {
      font-weight: 500;
      color: #303133;
      margin-bottom: 12px;
    }

    .student-result-item {
      padding: 10px 12px;
      border: 1px solid #ebeef5;
      border-radius: 4px;
      margin-bottom: 8px;

      &:hover {
        background-color: #f5f7fa;
      }

      .student-name {
        font-weight: 500;
        margin-right: 12px;
      }

      .student-email {
        color: #909399;
        font-size: 13px;
      }
    }
  }

  .no-results {
    margin-top: 16px;
  }
}

// ---- Tabs Orange Theme ----
::v-deep .el-tabs__item.is-active {
  color: #FF8833;
}

::v-deep .el-tabs__item:hover {
  color: #FF8833;
}

::v-deep .el-tabs__active-bar {
  background-color: #FF8833;
}

// ---- Share Claim Link Button ----
.action-bar {
  .el-button--default {
    &:hover,
    &:focus {
      color: #FF8833;
      border-color: #FFB980;
      background-color: #FFF3E6;
    }
  }
}

// ---- Table Styles ----
::v-deep .el-table .custom-row-bg {
  // background: #FF8833;
}

::v-deep .el-table .custom-head-cell {
  background: #FF8833;
  color: white;
  box-sizing: border-box;
  font-family: GravityBold;
}
</style>
